import { useEffect, useRef, useState } from "react";
import { SelectedFile } from "../../types/PostSelection";
import { ChevronLeft, ChevronRight, Grid, Image, Plus } from "react-feather";
import { getMediaPath, getSignedUrl, MEDIA_SIZE } from "../../api/files";
import "./styles.css";
import { Modal } from "../Modal";
import { handleError } from "../../utils/handleError";
import { useKeyDown } from "../../utils/useKeyDown";
import { ImageThumbnail } from "../ImageThumbnail";
import cx from "classnames";

export function PhotoViewer({
  open,
  close,
  selected,
  images,
  onSelectionChange,
  onCreatePost,
}: {
  open: boolean;
  close: () => void;
  selected: string | null;
  images: SelectedFile[];
  onSelectionChange?: (files: SelectedFile[]) => void;
  onCreatePost?: (selectedFiles: SelectedFile[]) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [imgIndex, setImgIndex] = useState(-1);
  const [imgUrl, setImgUrl] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const [allImages, setAllImages] = useState<SelectedFile[]>(images);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [showOnlyUnused, setShowOnlyUnused] = useState(false);
  const [activeTab, setActiveTab] = useState<"viewer" | "grid">("viewer");
  const [thumbnailSize, setThumbnailSize] = useState(200);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOverItem, setDraggedOverItem] = useState<string | null>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  console.log("Active tab:", activeTab);

  // Utility functions
  const updateImageSelection = (img: SelectedFile, isSelected: boolean) => {
    const imageIndex = allImages.findIndex((image) => image._id === img._id);
    // const updatedImg = { ...img };

    if (isSelected) {
      allImages[imageIndex].isSelected = true;
      allImages[imageIndex].selectedAt = new Date().getTime();
    } else {
      allImages[imageIndex].isSelected = false;
      allImages[imageIndex].selectedAt = undefined;
    }

    const updatedImages = [
      ...allImages.slice(0, imageIndex),
      allImages[imageIndex],
      ...allImages.slice(imageIndex + 1),
    ];

    setAllImages(updatedImages);
    onSelectionChange?.(updatedImages);
  };

  const handleDragStart = (e: React.DragEvent, imgId: string) => {
    e.dataTransfer.setData("text/plain", imgId);
    e.dataTransfer.effectAllowed = "move";
    setDraggedItem(imgId);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e: React.DragEvent, currentImgId: string) => {
    e.preventDefault();
    if (draggedItem && draggedItem !== currentImgId) {
      setDraggedOverItem(currentImgId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    setDraggedOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");
    const draggedId = e.dataTransfer.getData("text/plain");

    if (draggedId && targetId && draggedId !== targetId) {
      const selectedImages = allImages
        .filter((img) => img.isSelected)
        .sort((a, b) => a.selectedAt! - b.selectedAt!);

      const draggedIndex = selectedImages.findIndex(
        (img) => img._id === draggedId
      );
      const targetIndex = selectedImages.findIndex(
        (img) => img._id === targetId
      );

      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Reorder the selected images array
        const reorderedSelected = [...selectedImages];
        const [draggedItem] = reorderedSelected.splice(draggedIndex, 1);
        reorderedSelected.splice(targetIndex, 0, draggedItem);

        // Update selectedAt timestamps to reflect new order
        const baseTime = Date.now();
        reorderedSelected.forEach((selectedImg, idx) => {
          selectedImg.selectedAt = baseTime + idx;
        });

        // Update allImages with new timestamps
        const updatedAllImages = allImages.map((img) => {
          const reorderedImg = reorderedSelected.find(
            (sel) => sel._id === img._id
          );
          return reorderedImg || img;
        });

        setAllImages(updatedAllImages);
        onSelectionChange?.(updatedAllImages);
      }
    }
    setDraggedItem(null);
  };

  // Get the current images to display based on the toggle
  const displayImages = showOnlySelected
    ? allImages
        .filter((img) => img.isSelected)
        .sort((a, b) => a.selectedAt! - b.selectedAt!)
    : allImages;

  const selectedImg = displayImages[imgIndex];
  const isVideo = selectedImg?.name?.includes(".video-thumbnail.jpg");

  useEffect(
    function () {
      if (open && selected && imgIndex === -1) {
        ref.current?.showModal();
        setImgIndex(displayImages?.findIndex((i) => i._id === selected));
      }
    },
    [open, selected, displayImages, imgIndex]
  );

  useEffect(
    function () {
      if (selectedImg && activeTab === "viewer") {
        console.log("GETTINGS IMAGES URL", selectedImg);
        setLoading(true);
        const isVideo = selectedImg.name.includes(".video-thumbnail.jpg");
        getFullResoltionImage(selectedImg, isVideo)
          .then((url) => {
            setImgUrl(url);
            console.log("GOT IMAGE URLS", url);
          })
          .catch((e) => handleError(e));
      }
    },
    [selectedImg, activeTab]
  );

  // Reset image index when view mode changes or when trying to maintain current image
  useEffect(() => {
    // Only reset index when showOnlySelected changes, not when selection state changes
    if (displayImages.length > 0) {
      // If we have a current image, try to maintain it in the new view
      if (imgIndex >= 0 && imgIndex < displayImages.length) {
        const currentImg = displayImages[imgIndex];
        if (currentImg) {
          // Current image is still valid, keep the same index
          return;
        }
      }

      // If no valid current image, find the initially selected one
      const index = displayImages.findIndex((i) => i._id === selected);
      setImgIndex(index >= 0 ? index : 0);
    } else {
      setImgIndex(-1);
    }
  }, [
    showOnlySelected,
    displayImages.length,
    displayImages,
    selected,
    imgIndex,
  ]);

  // Scroll thumbnails to keep current image centered
  useEffect(() => {
    if (thumbnailsRef.current && imgIndex >= 0 && activeTab === "viewer") {
      // Get the inner flex container that contains the thumbnails
      const flexContainer = thumbnailsRef.current.querySelector(
        ".flex.items-center.gap-2"
      ) as HTMLElement;
      if (flexContainer && flexContainer.children[imgIndex]) {
        const thumbnailElement = flexContainer.children[
          imgIndex
        ] as HTMLElement;
        thumbnailElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [imgIndex, activeTab]);

  const onImageLoad = () => {
    setLoading(false);
  };

  const closeModal = () => {
    ref.current?.close();
    setImgIndex(-1);
    close();
  };

  const prevImg = () => {
    setImgIndex((i) => (i === 0 ? displayImages.length - 1 : i - 1));
  };

  const nextImg = () => {
    setImgIndex((i) => (i === displayImages.length - 1 ? 0 : i + 1));
  };

  const toggleSelection = () => {
    console.log("TOGGLE SELECTION", selectedImg);
    if (selectedImg) {
      const imageIndex = allImages.findIndex(
        (img) => img._id === selectedImg._id
      );
      if (selectedImg.isSelected) {
        selectedImg.isSelected = false;
        selectedImg.selectedAt = undefined;
      } else {
        selectedImg.isSelected = true;
        selectedImg.selectedAt = new Date().getTime();
      }
      const updatedImages = [
        ...allImages.slice(0, imageIndex),
        selectedImg,
        ...allImages.slice(imageIndex + 1),
      ];
      setAllImages(updatedImages);
      onSelectionChange?.(updatedImages);
    }
  };

  // Calculate selected images count and current position
  const selectedImages = allImages.filter((img) => img.isSelected);
  const selectedCount = selectedImages.length;
  const currentPosition =
    allImages
      .filter((img) => img.selectedAt)
      .sort((a, b) => a.selectedAt! - b.selectedAt!)
      .findIndex((img) => img._id === selectedImg?._id) + 1;

  useKeyDown(prevImg, ["ArrowLeft"]);
  useKeyDown(nextImg, ["ArrowRight"]);

  return (
    <Modal
      show={open}
      onClose={closeModal}
      className="lg:max-w-11/12 overflow-hidden"
      hideHeader
    >
      <>
        {/* Tab Navigation */}
        <div className="absolute top-6 left-8 right-12 z-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="tabs tabs-boxed bg-base-100 rounded-lg shadow-lg">
              <button
                className={`tab ${activeTab === "viewer" ? "tab-active" : ""}`}
                onClick={() => setActiveTab("viewer")}
              >
                <Image className="w-4 h-4 mr-1" />
                Viewer
              </button>
              <button
                className={`tab ${activeTab === "grid" ? "tab-active" : ""}`}
                onClick={() => setActiveTab("grid")}
              >
                <Grid className="w-4 h-4 mr-1" />
                Grid
              </button>
            </div>

            {onCreatePost && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  const selectedFiles = allImages.filter((f) => f.isSelected);
                  onCreatePost(
                    selectedFiles.sort((a, b) => a.selectedAt! - b.selectedAt!)
                  );
                }}
                disabled={!allImages.some((f) => f.isSelected)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Create Post
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Thumbnail size slider */}
            {activeTab === "grid" && (
              <div className="flex items-center justify-center gap-4">
                <input
                  type="range"
                  min="100"
                  max="360"
                  step="10"
                  value={thumbnailSize}
                  onChange={(e) => setThumbnailSize(parseInt(e.target.value))}
                  className="range range-neutral range-sm w-48"
                />
                <span className="text-sm w-12">{thumbnailSize}px</span>
              </div>
            )}

            <label className="cursor-pointer flex items-center gap-2 bg-base-100 p-2 rounded-lg shadow-lg">
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={showOnlyUnused}
                onChange={(e) => setShowOnlyUnused(e.target.checked)}
              />
              <span className="text-sm">Show only unused</span>
            </label>

            <label className="cursor-pointer flex items-center gap-2 bg-base-100 p-2 rounded-lg shadow-lg">
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={showOnlySelected}
                onChange={(e) => setShowOnlySelected(e.target.checked)}
              />
              <span className="text-sm">Show only selected</span>
            </label>
          </div>
        </div>

        {activeTab === "viewer" ? (
          <div className="photo-viewer-grid h-full w-full max-h-full">
            <button
              type="button"
              className="btn btn-circle photo-viewer-grid-button1"
              onClick={prevImg}
            >
              <ChevronLeft />
            </button>

            <div className="photo-viewer-grid-main h-full relative">
              {loading && (
                <div className="absolute h-full w-full">
                  <div className="flex justify-center items-center h-full w-full">
                    <span className="loading loading-spinner loading-lg text-primary" />
                  </div>
                </div>
              )}
              {imgUrl && !isVideo && (
                <img
                  src={imgUrl}
                  alt={selectedImg?.name || "Image"}
                  className="object-scale-down max-h-full"
                  onLoad={onImageLoad}
                />
              )}
              {imgUrl && isVideo && (
                <video
                  src={imgUrl}
                  className="max-h-full"
                  onLoadedMetadata={onImageLoad}
                  controls
                />
              )}
            </div>

            <button
              type="button"
              className="btn btn-circle photo-viewer-grid-button2"
              onClick={nextImg}
            >
              <ChevronRight />
            </button>

            {/* Thumbnails row */}
            <div className="photo-viewer-grid-thumbnails max-w-full pt-4 pb-2 px-8">
              <div className="flex justify-center">
                <div
                  ref={thumbnailsRef}
                  className="p-2 bg-base-100/80 rounded-lg shadow-lg max-w-full overflow-x-auto scrollbar-hide"
                >
                  <div className="flex items-center gap-2 w-fit">
                    {displayImages.map(
                      (img, index) =>
                        (showOnlyUnused ? !img.isUsed : true) && (
                          <ImageThumbnail
                            key={img._id}
                            id={parseInt(img._id || "0")}
                            title={img.name}
                            url={img.url}
                            minetype={img.mine_type || "image/jpeg"}
                            rating={img.rating}
                            isUsed={img.isUsed}
                            isSelected={img.isSelected || false}
                            currentSelection={imgIndex === index}
                            selectedOrder={
                              selectedImages.findIndex(
                                (s) => s._id === img._id
                              ) + 1
                            }
                            onSelect={() => setImgIndex(index)}
                            size="small"
                          />
                        )
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="photo-viewer-grid-footer flex items-center gap-4">
              <span>{selectedImg?.name}</span>
              {selectedCount > 0 && (
                <div className="badge badge-neutral">
                  {selectedCount} selected
                </div>
              )}
              {selectedImg?.isSelected && currentPosition > 0 && (
                <div className="badge badge-primary">#{currentPosition}</div>
              )}
              <label className="label cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedImg?.isSelected || false}
                  onChange={toggleSelection}
                />
                <span className="cursor-pointer">
                  {selectedImg?.isSelected ? "Selected" : "Select"}
                </span>
              </label>
            </div>
          </div>
        ) : (
          <div
            className={cx("relative max-h-[90vh] w-full pt-14 flex flex-col")}
          >
            {/* Large thumbnails grid */}
            <div
              className={cx(
                "flex-1 overflow-y-auto rounded-t-lg",
                selectedCount > 0 && "pb-[180px]"
              )}
            >
              <div
                className="grid gap-6 justify-items-center items-center pb-6"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${thumbnailSize}px, 1fr))`,
                }}
              >
                {displayImages.map(
                  (img, index) =>
                    (showOnlyUnused ? !img.isUsed : true) && (
                      <ImageThumbnail
                        key={img._id}
                        id={parseInt(img._id || "0")}
                        title={img.name}
                        url={img.url}
                        minetype={img.mine_type || "image/jpeg"}
                        rating={img.rating}
                        isUsed={img.isUsed}
                        isSelected={img.isSelected || false}
                        currentSelection={false}
                        selectedOrder={
                          selectedImages.findIndex((s) => s._id === img._id) + 1
                        }
                        onSelect={() =>
                          updateImageSelection(img, !img.isSelected)
                        }
                        size="custom"
                        customSize={`${thumbnailSize}px`}
                        defaultAspectRatio
                      />
                    )
                )}
              </div>
            </div>
          </div>
        )}

        {/* Selected images row at bottom */}
        {activeTab === "grid" && selectedCount > 0 && (
          <div className="fixed bottom-0 right-0 left-0 px-6 py-4 backdrop-blur-3xl drop-shadow-2xl z-10">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide justify-center pb-2">
              {allImages
                .filter((img) => img.isSelected)
                .sort((a, b) => a.selectedAt! - b.selectedAt!)
                .map((img, index) => (
                  <div
                    key={img._id}
                    className={cx(
                      "relative flex-shrink-0 cursor-move transition-all duration-200",
                      draggedItem === img._id && "opacity-50 scale-95 rotate-3",
                      draggedOverItem === img._id && "drag-over scale-110"
                    )}
                    draggable
                    onDragStart={(e) => handleDragStart(e, img._id || "")}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, img._id || "")}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, img._id || "")}
                  >
                    <ImageThumbnail
                      id={parseInt(img._id || "0")}
                      title={img.name}
                      url={img.url}
                      minetype={img.mine_type || "image/jpeg"}
                      rating={img.rating}
                      isUsed={img.isUsed}
                      isSelected={true}
                      currentSelection={false}
                      selectedOrder={
                        selectedImages.findIndex((s) => s._id === img._id) + 1
                      }
                      onSelect={() => updateImageSelection(img, false)}
                      size="custom"
                      customSize="150px"
                      defaultAspectRatio
                      hideSelected
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </>
    </Modal>
  );
}

async function getFullResoltionImage(img: SelectedFile, isVideo: boolean) {
  if (isVideo) {
    const imagePath = await getMediaPath(MEDIA_SIZE.video);
    return getSignedUrl(
      imagePath + "/" + img.name.replace(".video-thumbnail.jpg", "")
    );
  }

  const path = img.imageDerivatives?.derivatives["2400px"]?.path;
  if (!path) {
    throw new Error(`2400px image derivative is unavailable for ${img.name}`);
  }

  return getSignedUrl(path);
}
